import Phaser from 'phaser'
import { sharedInstance as events, getPersistentBudgetState } from './EventCenter'

type BudgetPayload = {
	current: number
	max: number
}

type CoinCollectedPayload = {
	amount: number
	rewardType: string
}

type LevelTimePayload = {
	remainingMs: number
	visible: boolean
}

type FinalBossHealthPayload = {
	visible: boolean
	current: number
	max: number
}

type CoinLogEntry = {
	id: number
	text: Phaser.GameObjects.Text
	expireEvent: Phaser.Time.TimerEvent
}

const INITIAL_BUDGET = 3000
const PIXEL_FONT_FAMILY = '"Press Start 2P", system-ui'
const PIXEL_FONT_LOAD_RULE = '18px "Press Start 2P"'
const BUDGET_TEXT_HEIGHT_MULTIPLIER = 1.25
const BUDGET_WARNING_THRESHOLD = 0.5
const BUDGET_DANGER_THRESHOLD = 0.2
const BUDGET_FILL_COLOR_OK = 0x20e313
const BUDGET_FILL_COLOR_WARNING = 0xffd54f
const BUDGET_FILL_COLOR_DANGER = 0xe53935
const FINAL_BOSS_FILL_COLOR = 0x20e313
const FINAL_BOSS_FILL_COLOR_WARNING = 0xf57c00
const FINAL_BOSS_FILL_COLOR_DANGER = 0xe53935
const FINAL_BOSS_WARNING_THRESHOLD = 0.55
const FINAL_BOSS_DANGER_THRESHOLD = 0.15
const COIN_LOG_ENTRY_DURATION_MS = 5000
const COIN_LOG_OFFSET_Y = 10
const COIN_LOG_SPACING = 22
const DEFAULT_COIN_REWARD_TYPE = 'bizum'
const LEVEL_TIME_CRITICAL_THRESHOLD_SECONDS = 30
const LEVEL_TIME_NORMAL_COLOR = '#ffffff'
const LEVEL_TIME_CRITICAL_COLOR = '#ff3b30'
const LEVEL_TIME_BLINK_ALPHA_MIN = 0.7
const LEVEL_TIME_BLINK_DURATION_MS = 260

const COIN_REWARD_LABELS: Record<string, string> = {
	bizum: 'Bizum',
	nomina: 'Nomina',
	cashback: 'Cashback',
	devolucion_comision: 'Devolucion de comision'
}

export default class UI extends Phaser.Scene
{
	private graphics!: Phaser.GameObjects.Graphics
	private budgetText!: Phaser.GameObjects.Text
	private levelTimeText!: Phaser.GameObjects.Text
	private levelTimeBlinkTween?: Phaser.Tweens.Tween
	private finalBossHealthText!: Phaser.GameObjects.Text
	private heartIcon?: Phaser.GameObjects.Image
	private pixelFontReady = false
	private coinLogs: CoinLogEntry[] = []
	private nextCoinLogId = 1
	private budgetBarLogAnchorX = 46
	private budgetBarLogAnchorY = 33

	private budget = INITIAL_BUDGET
	private maxBudget = INITIAL_BUDGET
	private levelTimeRemainingMs = 0
	private levelTimeVisible = false
	private finalBossHealthVisible = false
	private finalBossHealthCurrent = 100
	private finalBossHealthMax = 100

	constructor()
	{
		super({
			key: 'ui'
		})
	}

	init()
	{
		const persistedBudget = getPersistentBudgetState()
		if (persistedBudget)
		{
			this.budget = persistedBudget.current
			this.maxBudget = persistedBudget.max
		}
		else
		{
			this.budget = INITIAL_BUDGET
			this.maxBudget = INITIAL_BUDGET
		}
	}

	create()
	{
		this.graphics = this.add.graphics()
		const isCompact = this.scale.width < 900

		this.heartIcon = this.textures.exists('heart')
			? this.add.image(22, 22, 'heart').setDisplaySize(isCompact ? 18 : 22, isCompact ? 18 : 22)
			: this.textures.exists('health')
				? this.add.image(22, 22, 'health').setDisplaySize(isCompact ? 18 : 22, isCompact ? 18 : 22)
			: undefined

		this.budgetText = this.add.text(0, 22, `${this.budget}€`, {
			fontFamily: PIXEL_FONT_FAMILY,
			fontSize: isCompact ? '14px' : '16px',
			color: '#ffffff',
			stroke: '#000000',
			strokeThickness: 3
		}).setOrigin(0, 0.5)

		this.levelTimeText = this.add.text(0, 22, '00:00', {
			fontFamily: PIXEL_FONT_FAMILY,
			fontSize: isCompact ? '16px' : '18px',
			color: LEVEL_TIME_NORMAL_COLOR,
			stroke: '#000000',
			strokeThickness: 3
		}).setOrigin(1, 0.5).setVisible(false)

		this.finalBossHealthText = this.add.text(0, 22, 'The Boss', {
			fontFamily: PIXEL_FONT_FAMILY,
			fontSize: isCompact ? '12px' : '14px',
			color: '#ffffff',
			strokeThickness: 0
		}).setOrigin(1, 0.5).setVisible(false)

		this.setBudgetBar(this.budget, this.maxBudget)
		this.updateLevelTimeVisual()
		this.refreshBudgetTextFontWhenReady()

		events.on('budget-changed', this.handleBudgetChanged, this)
		events.on('level-time-changed', this.handleLevelTimeChanged, this)
		events.on('coin-collected', this.handleCoinCollected, this)
		events.on('final-boss-health-changed', this.handleFinalBossHealthChanged, this)

		this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
			events.off('budget-changed', this.handleBudgetChanged, this)
			events.off('level-time-changed', this.handleLevelTimeChanged, this)
			events.off('coin-collected', this.handleCoinCollected, this)
			events.off('final-boss-health-changed', this.handleFinalBossHealthChanged, this)
			this.stopLevelTimeCriticalBlink()
			this.clearCoinLogs()
		})
	}

	private setBudgetBar(value: number, maxValue: number)
	{
		const isCompact = this.scale.width < 900
		const heartCenterX = 22
		const heartSize = isCompact ? 18 : 22
		const availableWidth = this.scale.width - 140
		const width = Math.floor(Phaser.Math.Clamp(availableWidth, 90, 250) * 0.75)
		const height = isCompact ? 18 : 22
		const x = isCompact ? 42 : 46
		const y = isCompact ? 13 : 11
		const percent = maxValue > 0
			? Phaser.Math.Clamp(value / maxValue, 0, 1)
			: 0

		const boxToHeartGap = x - (heartCenterX + (heartSize * 0.5))
		const textX = x + width + boxToHeartGap
		this.budgetText.setText(`${Math.round(value)}€`)
		this.fitBudgetTextHeight(height)
		this.budgetText.setPosition(textX, y + (height * 0.6))
		this.levelTimeText.setPosition(this.scale.width - (isCompact ? 16 : 20), y + (height * 0.6))
		this.budgetBarLogAnchorX = x
		this.budgetBarLogAnchorY = y + height + COIN_LOG_OFFSET_Y

		this.graphics.clear()

		const fillInset = 2
		const barX = x + fillInset
		const barY = y + fillInset
		const barWidth = Math.max(0, width - (fillInset * 2))
		const barHeight = Math.max(0, height - (fillInset * 2))
		const borderThickness = 3
		const borderCornerCut = 3

		this.drawPixelCutFrame(
			barX - borderThickness,
			barY - borderThickness,
			barWidth + (borderThickness * 2),
			barHeight + (borderThickness * 2),
			0x000000,
			borderCornerCut,
			borderThickness
		)

		if (percent > 0)
		{
			const fillColor = percent < BUDGET_DANGER_THRESHOLD
				? BUDGET_FILL_COLOR_DANGER
				: percent < BUDGET_WARNING_THRESHOLD
					? BUDGET_FILL_COLOR_WARNING
					: BUDGET_FILL_COLOR_OK
			const fillWidth = Math.floor(barWidth * percent)
			this.drawPixelCutRect(
				barX,
				barY,
				Math.max(0, fillWidth),
				barHeight,
				fillColor,
				1
			)
		}

		this.drawFinalBossHealthBar()

		this.layoutCoinLogs(false)
	}

	private drawFinalBossHealthBar()
	{
		if (!this.finalBossHealthVisible)
		{
			if (this.finalBossHealthText)
			{
				this.finalBossHealthText.setVisible(false)
			}
			return
		}

		const isCompact = this.scale.width < 900
		const width = Math.floor(Phaser.Math.Clamp(this.scale.width - 140, 90, 250) * 0.75)
		const height = isCompact ? 18 : 22
		const x = this.scale.width - width - (isCompact ? 42 : 46)
		const y = isCompact ? 13 : 11
		const maxValue = Math.max(1, this.finalBossHealthMax)
		const percent = Phaser.Math.Clamp(this.finalBossHealthCurrent / maxValue, 0, 1)

		const fillInset = 2
		const barX = x + fillInset
		const barY = y + fillInset
		const barWidth = Math.max(0, width - (fillInset * 2))
		const barHeight = Math.max(0, height - (fillInset * 2))
		const borderThickness = 3
		const borderCornerCut = 3

		this.drawPixelCutFrame(
			barX - borderThickness,
			barY - borderThickness,
			barWidth + (borderThickness * 2),
			barHeight + (borderThickness * 2),
			0x000000,
			borderCornerCut,
			borderThickness
		)

		if (percent > 0)
		{
			const fillColor = percent < FINAL_BOSS_DANGER_THRESHOLD
				? FINAL_BOSS_FILL_COLOR_DANGER
				: percent < FINAL_BOSS_WARNING_THRESHOLD
					? FINAL_BOSS_FILL_COLOR_WARNING
					: FINAL_BOSS_FILL_COLOR
			const fillWidth = Math.floor(barWidth * percent)
			this.drawPixelCutRect(
				barX,
				barY,
				Math.max(0, fillWidth),
				barHeight,
				fillColor,
				1
			)
		}

		this.finalBossHealthText.setText('The Boss')
		this.finalBossHealthText.setPosition(x - 10, y + (height * 0.6))
		this.finalBossHealthText.setVisible(true)
	}

	private handleCoinCollected(payload: CoinCollectedPayload)
	{
		const entryText = `+${Math.round(payload.amount)}€`
		const isCompact = this.scale.width < 900
		const entry = this.add.text(this.budgetBarLogAnchorX, this.budgetBarLogAnchorY, entryText, {
			fontFamily: PIXEL_FONT_FAMILY,
			fontSize: isCompact ? '16px' : '18px',
			color: '#ffffff',
			stroke: '#000000',
			strokeThickness: 3
		}).setOrigin(0, 0)

		const id = this.nextCoinLogId++
		const expireEvent = this.time.delayedCall(COIN_LOG_ENTRY_DURATION_MS, () => {
			this.removeCoinLogById(id)
		})

		this.coinLogs.push({
			id,
			text: entry,
			expireEvent
		})

		this.layoutCoinLogs(true)
	}

	private removeCoinLogById(id: number)
	{
		const index = this.coinLogs.findIndex(log => log.id === id)
		if (index < 0)
		{
			return
		}

		const [removedLog] = this.coinLogs.splice(index, 1)
		if (removedLog.expireEvent && !removedLog.expireEvent.hasDispatched)
		{
			removedLog.expireEvent.remove(false)
		}
		removedLog.text.destroy()

		this.layoutCoinLogs(true)
	}

	private clearCoinLogs()
	{
		this.coinLogs.forEach(log => {
			if (log.expireEvent && !log.expireEvent.hasDispatched)
			{
				log.expireEvent.remove(false)
			}
			log.text.destroy()
		})
		this.coinLogs = []
	}

	private layoutCoinLogs(animate: boolean)
	{
		this.coinLogs.forEach((log, index) => {
			const targetX = this.budgetBarLogAnchorX
			const targetY = this.budgetBarLogAnchorY + (index * COIN_LOG_SPACING)

			if (!animate)
			{
				log.text.setPosition(targetX, targetY)
				return
			}

			this.tweens.killTweensOf(log.text)
			this.tweens.add({
				targets: log.text,
				x: targetX,
				y: targetY,
				duration: 160,
				ease: Phaser.Math.Easing.Sine.Out
			})
		})
	}

	private drawPixelCutRect(x: number, y: number, width: number, height: number, color: number, cornerCut: number)
	{
		if (width <= 0 || height <= 0)
		{
			return
		}

		const maxCut = Math.floor((Math.min(width, height) - 1) / 2)
		const safeCut = Phaser.Math.Clamp(cornerCut, 0, Math.max(0, maxCut))
		this.graphics.fillStyle(color)

		if (safeCut === 0)
		{
			this.graphics.fillRect(x, y, width, height)
			return
		}

		const middleWidth = width - (safeCut * 2)
		const sideHeight = height - (safeCut * 2)

		if (middleWidth > 0)
		{
			this.graphics.fillRect(x + safeCut, y, middleWidth, height)
		}

		if (sideHeight > 0)
		{
			this.graphics.fillRect(x, y + safeCut, safeCut, sideHeight)
			this.graphics.fillRect(x + width - safeCut, y + safeCut, safeCut, sideHeight)
		}
	}

	private drawPixelCutFrame(
		x: number,
		y: number,
		width: number,
		height: number,
		color: number,
		cornerCut: number,
		thickness: number
	)
	{
		if (width <= 0 || height <= 0 || thickness <= 0)
		{
			return
		}

		const t = Math.max(1, Math.floor(thickness))
		const maxCut = Math.floor((Math.min(width, height) - 1) / 2)
		const safeCut = Phaser.Math.Clamp(cornerCut, 0, Math.max(0, maxCut))
		const horizontalWidth = width - (safeCut * 2)
		const verticalHeight = height - (safeCut * 2)

		this.graphics.fillStyle(color)

		if (horizontalWidth > 0)
		{
			this.graphics.fillRect(x + safeCut, y, horizontalWidth, t)
			this.graphics.fillRect(x + safeCut, y + height - t, horizontalWidth, t)
		}

		if (verticalHeight > 0)
		{
			this.graphics.fillRect(x, y + safeCut, t, verticalHeight)
			this.graphics.fillRect(x + width - t, y + safeCut, t, verticalHeight)
		}
	}

	private fitBudgetTextHeight(targetHeight: number)
	{
        targetHeight = parseInt(this.budgetText.style.fontSize);
		const baseFontSize = targetHeight
		this.budgetText.setScale(1)
		this.budgetText.setFontSize(baseFontSize)

		const measuredHeight = this.budgetText.height
		if (measuredHeight <= 0)
		{
			return
		}

		const fitScale = Phaser.Math.Clamp((targetHeight * BUDGET_TEXT_HEIGHT_MULTIPLIER) / measuredHeight, 0.5, 3)
		this.budgetText.setScale(fitScale)
	}

	private refreshBudgetTextFontWhenReady()
	{
		this.ensurePixelFontLoaded().then(() => {
			if (!this.budgetText || !this.budgetText.active)
			{
				return
			}

			this.budgetText.setFontFamily(PIXEL_FONT_FAMILY)
			this.setBudgetBar(this.budget, this.maxBudget)
		})
	}

	private ensurePixelFontLoaded(): Promise<void>
	{
		if (this.pixelFontReady)
		{
			return Promise.resolve()
		}

		const fontSet = document.fonts
		if (!fontSet)
		{
			this.pixelFontReady = true
			return Promise.resolve()
		}

		return fontSet.load(PIXEL_FONT_LOAD_RULE)
			.then(() => fontSet.ready)
			.then(() => {
				this.pixelFontReady = true
			})
			.catch(() => {
				this.pixelFontReady = true
			})
	}

	private handleBudgetChanged(payload: BudgetPayload)
	{
		this.tweens.addCounter({
			from: this.budget,
			to: payload.current,
			duration: 260,
			ease: Phaser.Math.Easing.Sine.InOut,
			onUpdate: tween => {
				const tweenBudget = tween.getValue()
				this.setBudgetBar(tweenBudget, payload.max)
			}
		})

		this.budget = payload.current
		this.maxBudget = payload.max
	}

	private handleLevelTimeChanged(payload: LevelTimePayload)
	{
		this.levelTimeRemainingMs = Math.max(0, Math.floor(payload.remainingMs))
		this.levelTimeVisible = payload.visible === true
		this.updateLevelTimeVisual()
	}

	private handleFinalBossHealthChanged(payload: FinalBossHealthPayload)
	{
		this.finalBossHealthVisible = payload.visible === true
		this.finalBossHealthCurrent = Math.max(0, Math.round(payload.current))
		this.finalBossHealthMax = Math.max(1, Math.round(payload.max))
		this.setBudgetBar(this.budget, this.maxBudget)
	}

	private updateLevelTimeVisual()
	{
		if (!this.levelTimeText)
		{
			return
		}

		if (!this.levelTimeVisible)
		{
			this.stopLevelTimeCriticalBlink()
			this.levelTimeText.setVisible(false)
			return
		}

		const remainingSeconds = Math.max(0, Math.floor(this.levelTimeRemainingMs / 1000))
		const isCritical = remainingSeconds > 0 && remainingSeconds <= LEVEL_TIME_CRITICAL_THRESHOLD_SECONDS
		this.levelTimeText.setText(this.formatElapsedTime(this.levelTimeRemainingMs))
		this.levelTimeText.setVisible(true)
		this.levelTimeText.setColor(isCritical ? LEVEL_TIME_CRITICAL_COLOR : LEVEL_TIME_NORMAL_COLOR)

		if (isCritical)
		{
			if (!this.levelTimeBlinkTween)
			{
				this.levelTimeBlinkTween = this.tweens.add({
					targets: this.levelTimeText,
					alpha: LEVEL_TIME_BLINK_ALPHA_MIN,
					duration: LEVEL_TIME_BLINK_DURATION_MS,
					ease: 'Sine.easeInOut',
					yoyo: true,
					repeat: -1
				})
			}
			return
		}

		this.stopLevelTimeCriticalBlink()
	}

	private stopLevelTimeCriticalBlink()
	{
		if (this.levelTimeBlinkTween)
		{
			this.levelTimeBlinkTween.stop()
			this.levelTimeBlinkTween = undefined
		}

		if (this.levelTimeText)
		{
			this.levelTimeText.setAlpha(1)
		}
	}

	private formatElapsedTime(elapsedMs: number): string
	{
		const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000))
		const minutes = Math.floor(totalSeconds / 60)
		const seconds = totalSeconds % 60
		return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
	}
}
